import React, { useCallback, useContext, useState } from "react";
import styled from "styled-components";
import { ProseMirror } from "@progress/kendo-react-editor";
import { getUserSignature } from "@coxauto-ui/cxm/communications/api";
import {
  ConversationContext,
  ConversationDetailsContext,
  useEmailReply
} from "../../../context";
import type { KendoEditorInternal } from "../utils/domHelpers";

const SignatureButton = styled.button`
  padding: 6px 8px;
  background: transparent;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: background-color 0.2s;

  &:hover {
    background-color: #e9ecef;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

export interface CustomSignatureButtonProps {
  view?: KendoEditorInternal["view"];
}

function appendAtEnd(view: KendoEditorInternal["view"], html: string): boolean {
  try {
    const state = view?.state;
    const dispatch = view?.dispatch;
    const schema = state?.schema;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const doc = state?.doc as any;

    if (!schema || !dispatch || !doc) return false;

    const parser = ProseMirror.DOMParser.fromSchema(schema);
    const element = document.createElement("div");
    element.innerHTML = html;
    const slice = parser.parseSlice(element);

    // Insert at end of document — does not touch current selection.
    // Kendo's onChange fires after dispatch, syncing React state automatically
    // (same pattern as useInsertAtCursor).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tr = (state as any).tr.insert(doc.content.size, slice.content);
    dispatch(tr);
    return true;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn("appendAtEnd failed, falling back to setReplyText", error);
    return false;
  }
}

function useSignatureItem(view?: KendoEditorInternal["view"]) {
  const [isInserting, setIsInserting] = useState(false);
  const { env, vinDealerId } = useContext(ConversationContext);
  const { setSignatureInsertError } = useContext(ConversationDetailsContext);
  const { setReplyText } = useEmailReply();

  const insertSignature = useCallback(async () => {
    setSignatureInsertError(null);
    setIsInserting(true);

    try {
      const response = await getUserSignature(env, vinDealerId);

      if (response.errorType) {
        setSignatureInsertError(response.errorType);
        return;
      }

      if (!response.emailSignature) {
        setSignatureInsertError("generic");
        return;
      }

      const signatureHtml = "<br><br><br>" + response.emailSignature;

      // Try ProseMirror insertion (preserves cursor position).
      // Kendo's onChange syncs state automatically after dispatch.
      const inserted = appendAtEnd(view, signatureHtml);

      if (!inserted) {
        // Fallback: state update (cursor may jump)
        setReplyText((prev: string) => prev + signatureHtml);
      }
    } catch {
      setSignatureInsertError("generic");
    } finally {
      setIsInserting(false);
    }
  }, [env, vinDealerId, view, setReplyText, setSignatureInsertError]);

  return { isInserting, insertSignature };
}

export const CustomSignatureButton: React.FC<CustomSignatureButtonProps> = ({
  view
}) => {
  const { isInserting, insertSignature } = useSignatureItem(view);

  return (
    <SignatureButton
      aria-label="Insert My Signature"
      data-testid="signature-button"
      disabled={isInserting}
      title="Insert My Signature"
      type="button"
      onClick={insertSignature}
    >
      <svg
        fill="#656565"
        height="20"
        viewBox="0 -960 960 960"
        width="20"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M563-491q73-54 114-118.5T718-738q0-32-10.5-47T679-800q-47 0-83 79.5T560-541q0 14 .5 26.5T563-491ZM120-120v-80h80v80h-80Zm160 0v-80h80v80h-80Zm160 0v-80h80v80h-80Zm160 0v-80h80v80h-80Zm160 0v-80h80v80h-80ZM136-280l-56-56 64-64-64-64 56-56 64 64 64-64 56 56-64 64 64 64-56 56-64-64-64 64Zm482-40q-30 0-55-11.5T520-369q-25 14-51.5 25T414-322l-28-75q28-10 53.5-21.5T489-443q-5-22-7.5-48t-2.5-56q0-144 57-238.5T679-880q52 0 85 38.5T797-734q0 86-54.5 170T591-413q7 7 14.5 10.5T621-399q26 0 60.5-33t62.5-87l73 34q-7 17-11 41t1 42q10-5 23.5-17t27.5-30l63 49q-26 36-60 58t-63 22q-21 0-37.5-12.5T733-371q-28 25-57 38t-58 13Z" />
      </svg>
    </SignatureButton>
  );
};

export default CustomSignatureButton;
